import { AppState } from '@/context/appContext';
import { AppStateAction, AppStateActions } from '@/reducers/appReducer';
import { AnnotationActionType } from '@/reducers/annotationsReducer';
import { FormsActionTypes } from '@/reducers/formsReducer';
import { ImageItemsReducerActionType } from '@/reducers/imagesReducer';
import { ShapesReducerActionType } from '@/reducers/shapesReducer';
import { DrawingTypes, EditableTextItem, FormTypes, Point } from '@/types';
import { ShapeTypes } from '@/types/shapes';
import { useEditorStore } from '@/components/PdfEditor/context/editorStore';
import { fontLabel } from '@/fonts/fontLabels';
import { type ActionEventType } from './eventLogger';
import {
  ActionTrace,
  resetActionTracking,
  trackAction,
  trackActionOnce,
  trackBurstAction,
  trackTrailingAction,
} from './actionTracker';

/**
 * Maps dispatched state actions onto in-editor action events.
 *
 * Tracking lives here rather than in the components that dispatch, so a single
 * chokepoint (App's dispatch wrapper) covers every call site and UI code stays free
 * of logging concerns.
 */

const SHAPE_EVENTS: Record<ShapeTypes, ActionEventType> = {
  [ShapeTypes.Rectangle]: 'DRAW_RECTANGLE',
  [ShapeTypes.Ellipse]: 'DRAW_CIRCLE',
  [ShapeTypes.Line]: 'DRAW_LINE',
  [ShapeTypes.Arrow]: 'DRAW_ARROW',
};

const FORM_EVENTS: Partial<Record<FormTypes, ActionEventType>> = {
  [FormTypes.TEXT]: 'ADD_TEXT_FORM',
  [FormTypes.TEXTAREA]: 'ADD_TEXTAREA_FORM',
};

const round = (value: number | undefined): number | undefined =>
  value === undefined ? undefined : Math.round(value * 100) / 100;

/** Page indexes are 0-based throughout the editor; traces report human page numbers. */
const toPageNumber = (pageIndex: number): number => pageIndex + 1;

/** Document-level context attached to every action. */
const documentTrace = (state: AppState): ActionTrace => ({
  fileId: state.uploadedFileId ?? undefined,
  pageCount: Object.keys(state.pagesMetadata).length,
  scale: round(state.scale),
});

const boundingBox = (points: Point[]): { width?: number; height?: number } => {
  if (points.length === 0) return {};

  const xs = points.map((p) => p.x);
  const ys = points.map((p) => p.y);

  return {
    width: round(Math.max(...xs) - Math.min(...xs)),
    height: round(Math.max(...ys) - Math.min(...ys)),
  };
};

const findTextItem = (state: AppState, pageNumber: number, id?: string): EditableTextItem | undefined => {
  if (id === undefined) return undefined;

  // textItems is sparse — pages whose text has not been parsed yet have no entry at all,
  // despite the Record type implying otherwise.
  const pageItems = state.textItems[pageNumber] as EditableTextItem[] | undefined;

  return pageItems?.find((item) => item.id === id);
};

const isBlank = (text: string | undefined): boolean => (text ?? '').trim() === '';

/**
 * A font as a trace reports it.
 */
interface FontTrace {
  id: string;
  label: string;
  isEmbedded: boolean;
}

/**
 * Label and registry are read off the editor store rather than the AppState this module
 * is handed: the catalog list and the document's embedded faces are editor-scoped state,
 * and neither is mirrored into the document context.
 */
const fontDescriptor = (fontName: string, isEmbedded: boolean): FontTrace => {
  const { documentFonts, knownFonts } = useEditorStore.getState();

  return { id: fontName, label: fontLabel(fontName, documentFonts, knownFonts), isEmbedded };
};

/**
 * Trace for a text box entering the document.
 *
 * A box tapped out on the canvas is added to state immediately, carrying a placeholder
 * space, and is removed again on blur if nothing was typed into it — so the dispatched
 * add is not yet a user action that changes the document. The add is therefore reported
 * from whichever moment the box first holds text: dispatch time for boxes that arrive
 * with content (duplicates), first commit for the blank ones.
 */
const addTextTrace = (state: AppState, pageNumber: number, item: EditableTextItem): ActionTrace => ({
  ...documentTrace(state),
  page: toPageNumber(pageNumber),
  // The block had no text before the add, so only the new side is reported.
  newText: item.text,
  currentFont: fontDescriptor(item.fontName, item.isEmbedded),
  fontSize: round(item.currentCanvasCoordinates.fontSize),
  isDuplicated: item.isDuplicated === true,
});

/**
 * The font the block is rendered with, reported on every text event so a trace always
 * says what was on screen — whether or not the action touched the font. `newFont` is
 * added only when the patch actually switches the font, so its presence marks a font
 * change and its absence means `currentFont` still describes the block afterwards.
 *
 * The switch is detected on the committed name, which is also why the id stays in the
 * trace: two subsets of one face share a label, so a diff of labels would drop a pick
 * that genuinely moved the block onto another face.
 */
const fontSwitched = (current: EditableTextItem, patch: Partial<EditableTextItem>): boolean =>
  patch.fontName !== undefined && patch.fontName !== current.fontName;

const fontTrace = (current: EditableTextItem, patch: Partial<EditableTextItem> = {}): ActionTrace => {
  const newFontName = fontSwitched(current, patch) ? patch.fontName : undefined;

  return {
    currentFont: fontDescriptor(current.fontName, current.isEmbedded),
    // A font pick always states the new tier; the fallback covers a patch that moves the
    // name without it, where the item keeps the flag it already had.
    ...(newFontName !== undefined
      ? { newFont: fontDescriptor(newFontName, patch.isEmbedded ?? current.isEmbedded) }
      : {}),
  };
};

/**
 * The style fields the patch actually changes, in trace form.
 *
 * Only changed fields are reported: a patch is often built by spreading the whole item
 * and overwriting one property, so its mere presence means nothing — reporting every
 * field it carries would put `isBold` on a font-size change and read as if the user had
 * pressed both. An absent field therefore means "untouched by this action".
 */
const changedStyle = (current: EditableTextItem, patch: Partial<EditableTextItem>): ActionTrace => {
  const fontSize = patch.currentCanvasCoordinates?.fontSize;

  return {
    ...(patch.isBold !== undefined && patch.isBold !== current.isBold ? { isBold: patch.isBold } : {}),
    ...(patch.isItalic !== undefined && patch.isItalic !== current.isItalic ? { isItalic: patch.isItalic } : {}),
    ...(patch.fillColor !== undefined && patch.fillColor !== current.fillColor ? { fillColor: patch.fillColor } : {}),
    ...(fontSize !== undefined && fontSize !== current.currentCanvasCoordinates.fontSize
      ? { fontSize: round(fontSize) }
      : {}),
  };
};

/**
 * Classifies a text-item update by diffing the patch against the item already in state,
 * so the kinds of "edit" are told apart by what actually changed rather than by which
 * payload shape the call site happened to build.
 *
 * A font pick is its own kind rather than a STYLE: picking a face is a different act
 * from restyling one, and it drags the rest of the block's typography with it — an
 * embedded face resets bold/italic to its own, so those arrive on the same patch.
 */
const classifyTextUpdate = (
  current: EditableTextItem,
  patch: Partial<EditableTextItem>,
): 'TYPING' | 'FONT' | 'STYLE' | 'MOVE' | null => {
  if (patch.text !== undefined && patch.text !== current.text) return 'TYPING';

  if (fontSwitched(current, patch)) return 'FONT';

  if (Object.keys(changedStyle(current, patch)).length > 0) return 'STYLE';

  const moved =
    patch.currentCanvasCoordinates !== undefined &&
    (patch.currentCanvasCoordinates.x !== current.currentCanvasCoordinates.x ||
      patch.currentCanvasCoordinates.y !== current.currentCanvasCoordinates.y);

  return moved ? 'MOVE' : null;
};

const mapAndTrack = (action: AppStateAction, state: AppState): void => {
  switch (action.type) {
    case ShapesReducerActionType.ADD_SHAPE: {
      const { type, isWhiteout, pageNumber, points, borderWidth } = action.payload;

      trackAction(isWhiteout ? 'ERASE' : SHAPE_EVENTS[type], {
        ...documentTrace(state),
        page: toPageNumber(pageNumber),
        ...boundingBox(points),
        borderWidth,
      });
      break;
    }

    case AnnotationActionType.ADD: {
      const { newAnnotationType, pageNumber, color, lineWidth } = action.payload;

      // Stroke-level: one annotation per pen stroke, so these are coalesced. No size is
      // reported — a freehand stroke's only dimensions are its raster bounding box, which
      // describes the canvas it landed on rather than anything the user chose.
      trackBurstAction(newAnnotationType === DrawingTypes.Highlight ? 'DRAW_HIGHLIGHTER' : 'DRAW_PEN', {
        ...documentTrace(state),
        page: toPageNumber(pageNumber),
        color,
        lineWidth,
      });
      break;
    }

    case ImageItemsReducerActionType.ADD: {
      const { pageNumber, imageMeta, kind } = action.payload;

      trackAction(kind === 'signature' ? 'ADD_SIGNATURE' : 'ADD_IMAGE', {
        ...documentTrace(state),
        page: toPageNumber(pageNumber),
        width: round(imageMeta.width),
        height: round(imageMeta.height),
      });
      break;
    }

    case FormsActionTypes.ADD_FORM_FIELD: {
      const { formField, pageNumber } = action.payload;
      const eventType = FORM_EVENTS[formField.type];
      if (!eventType) break;

      trackAction(eventType, {
        ...documentTrace(state),
        page: toPageNumber(pageNumber),
        width: round(formField.canvasCoordinates.width),
        height: round(formField.canvasCoordinates.height),
        fontSize: round(formField.canvasCoordinates.fontSize),
      });
      break;
    }

    case AppStateActions.ADD_TEXT_ITEM: {
      const { pageNumber, textItem } = action.payload;

      // Arrived blank: not an add yet, see addTextTrace. A box that gets text is
      // reported from its first commit instead (UPDATE_TEXT_ITEM below).
      if (isBlank(textItem.text)) break;

      trackAction('ADD_TEXT', addTextTrace(state, pageNumber, textItem));
      break;
    }

    case AppStateActions.UPDATE_TEXT_ITEM: {
      const { pageNumber, textItem } = action.payload;
      const current = findTextItem(state, pageNumber, textItem.id);
      if (!current) break;

      // A removed block is gone from the UI and nothing un-deletes it, so any further
      // patch reaching it is bookkeeping rather than something the user did.
      if (current.isDeleted) break;

      // Removal takes precedence over everything else the patch carries (it blanks the
      // text too, which would otherwise read as typing). Both the toolbar's delete
      // button and a blur that leaves the box empty land here.
      if (textItem.isDeleted === true) {
        // A box that never held text is removed on blur without the document having
        // changed — it was never reported as added, so it is not reported as deleted.
        if (!isBlank(current.text)) {
          trackAction('DELETE_TEXT', {
            ...documentTrace(state),
            page: toPageNumber(pageNumber),
            ...fontTrace(current),
            currentText: current.text,
          });
        }
        break;
      }

      const kind = classifyTextUpdate(current, textItem);
      if (kind === null) break;

      // First text committed to a box that was blank — that commit is the add (see
      // addTextTrace), so it is reported as one instead of as an edit. Blank to blank
      // is the abandoned-box case: the box is removed again on blur and the document
      // is left untouched, so nothing is reported at all.
      if (kind === 'TYPING' && isBlank(current.text)) {
        if (!isBlank(textItem.text)) {
          trackAction('ADD_TEXT', addTextTrace(state, pageNumber, { ...current, ...textItem }));
        }
        break;
      }

      const trace = {
        ...documentTrace(state),
        page: toPageNumber(pageNumber),
        kind,
        // On every kind, not just typing: it is what identifies the block that was acted
        // on, so a restyle or a move can be tied to the text it happened to.
        currentText: current.text,
        ...fontTrace(current, textItem),
      };

      // A font pick and a styling change are each a discrete choice, so every one counts.
      // Typing is dispatched on commit (the block's blur), not per keystroke, so it counts
      // every change too — including repeated edits of the same block. Dragging runs long
      // on a single block, so it logs once per block and resets on the next one.
      // Every field is reported by difference (see fontTrace and changedStyle), so a trace
      // lists what the action touched and nothing else.
      if (kind === 'TYPING') {
        // currentText comes from the shared trace, so both sides of the edit are reported
        // and a rendering error can be replayed against the exact text that produced it.
        trackAction('EDIT_TEXT', { ...trace, newText: textItem.text });
      } else if (kind === 'FONT') {
        // A FONT trace keeps the style fields as well: switching to an embedded face
        // resets bold/italic to that face's own, which is part of what the pick did.
        trackAction('EDIT_TEXT', { ...trace, ...changedStyle(current, textItem) });
      } else if (kind === 'STYLE') {
        const style = changedStyle(current, textItem);

        // The colour picker dispatches continuously through a drag and the size input on
        // every step, so the settled value is logged once the control goes quiet rather
        // than one row per intermediate value. The changed field is part of the scope, so
        // adjusting the colour never swallows a bold pressed a moment earlier.
        trackTrailingAction('EDIT_TEXT', `${kind}:${Object.keys(style).sort().join('+')}`, current.id, {
          ...trace,
          ...style,
        });
      } else {
        trackActionOnce('EDIT_TEXT', kind, current.id, trace);
      }
      break;
    }

    case AppStateActions.ADD_PAGE: {
      trackAction('ADD_PAGE', {
        ...documentTrace(state),
        page: toPageNumber(action.payload.pageIndex),
      });
      break;
    }

    case AppStateActions.DELETE_PAGE: {
      trackAction('DELETE_PAGE', {
        ...documentTrace(state),
        page: toPageNumber(action.payload.pageIndex),
      });
      break;
    }

    // A new document starts a fresh session: drop pending bursts and per-block memory
    // so counts never carry across documents. SET_DOCUMENT is included because loading
    // a document resets state inside the reducer without a separate RESET_STATE dispatch.
    case AppStateActions.RESET_STATE:
    case AppStateActions.SET_DOCUMENT: {
      resetActionTracking();
      break;
    }
  }
};

export const trackDispatchedAction = (action: AppStateAction, state: AppState): void => {
  // Tracking sits on the dispatch path, so a mapping bug must never take the editor
  // down with it.
  try {
    mapAndTrack(action, state);
  } catch (_ignore) {
    // nothing to do
  }
};
